import { type EntityId } from '@/domain/model';
import { type Scene } from '@/engine';

import { decodeImagesFor } from './decodeImages';
import { type PageSetup } from './page';
import { toPdf } from './pdf';
import { renderToBlob, type RasterOptions } from './raster';
import { toSvg } from './svg';

/**
 * Saving a drawing as a picture.
 *
 * Four formats, one entry point, because the choice a teacher makes is between *uses* — a
 * slide, a worksheet, a print — not between file structures. The differences that matter are
 * captured in the defaults below rather than left for the user to get wrong.
 *
 * This produces the bytes and stops there. Handing them to the browser is a different
 * concern in a different layer, and keeping the seam here is what lets every format be
 * tested without a download.
 */

export const DrawingFormat = {
  Png: 'png',
  Svg: 'svg',
  Pdf: 'pdf',
  Jpeg: 'jpeg',
} as const;
export type DrawingFormat = (typeof DrawingFormat)[keyof typeof DrawingFormat];

const MIME: Readonly<Record<DrawingFormat, string>> = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
};

export interface DrawingExportOptions {
  readonly format: DrawingFormat;
  /**
   * Export only these entities, cropped to them.
   *
   * What a teacher wanting the variation table *and* the curve as two files reaches for:
   * select one, export, select the other, export. One picture of everything cannot be cut
   * apart afterwards without a graphics editor.
   */
  readonly only?: ReadonlySet<EntityId> | undefined;
  readonly filename?: string;
  /** Blank space around the drawing, in world units. */
  readonly margin?: number;
  /** Output units per world unit. */
  readonly scale?: number;
  /**
   * Width ÷ height the picture is brought to, by adding blank space around the drawing.
   *
   * Passed through to `exportFrame`, where the reasoning is set out: a set of figures dropped
   * into one exam paper wants one shape, and the shape is reached by padding rather than by
   * stretching — squeezing a figure to fit would turn a circle into an ellipse.
   *
   * Not offered to PDF, which is already sized to paper. Two shapes competing for one page gives
   * a figure floating in the middle of a sheet inside margins nobody asked for; `page` is how a
   * PDF states its proportions.
   */
  readonly aspect?: number;
  /** Device pixels per output unit, for the raster formats. */
  readonly pixelRatio?: number;
  /**
   * Page colour. `null` means transparent, which the raster and vector formats support and
   * PDF does not — paper is not transparent, so PDF simply leaves the page unpainted.
   */
  readonly background?: string | null;
  /**
   * Paper, orientation and tiling. PDF only — the raster and SVG formats have no notion of
   * a sheet, and pretending otherwise would mean a PNG cropped to A4 for no reason.
   */
  readonly page?: PageSetup;
}

/**
 * Builds the file.
 *
 * Async for all four formats even though two are synchronous: `canvas.toBlob` is not, and a
 * caller that had to know which formats block would be a caller that breaks when a format
 * changes its mind.
 */
export async function buildDrawing(scene: Scene, options: DrawingExportOptions): Promise<Blob> {
  const { format, only } = options;
  const margin = options.margin;
  const scale = options.scale;
  const aspect = options.aspect;
  const background = options.background === undefined ? '#ffffff' : options.background;

  switch (format) {
    case DrawingFormat.Svg: {
      const svg = toSvg(scene, {
        ...(only === undefined ? {} : { only }),
        ...(margin === undefined ? {} : { margin }),
        ...(scale === undefined ? {} : { scale }),
        ...(aspect === undefined ? {} : { aspect }),
        // A transparent SVG is usually what a worksheet wants, so `null` is honoured here.
        ...(background === null ? {} : { background }),
      });
      return new Blob([svg], { type: MIME.svg });
    }

    case DrawingFormat.Pdf: {
      /*
       * The only asynchronous step in the vector path, and the only one that touches the DOM.
       * A PDF cannot carry a PNG without a decompressor this writer does not have, so the
       * pictures are decoded and re-encoded as JPEG here and handed to a serialiser that stays
       * pure. A document with no images pays nothing: the map comes back empty.
       */
      const images = await decodeImagesFor(scene);
      const bytes = toPdf(scene, {
        ...(only === undefined ? {} : { only }),
        ...(images.size === 0 ? {} : { images }),
        ...(margin === undefined ? {} : { margin }),
        ...(scale === undefined ? {} : { scale }),
        ...(background === null ? {} : { background }),
        ...(options.page === undefined ? {} : { page: options.page }),
      });
      // A fresh buffer: the typed array's own buffer may be a view into a larger one.
      return new Blob([bytes.slice().buffer], { type: MIME.pdf });
    }

    case DrawingFormat.Png:
    case DrawingFormat.Jpeg: {
      const raster: RasterOptions = {
        ...(only === undefined ? {} : { only }),
        ...(margin === undefined ? {} : { margin }),
        ...(scale === undefined ? {} : { scale }),
        ...(aspect === undefined ? {} : { aspect }),
        ...(options.pixelRatio === undefined ? {} : { pixelRatio: options.pixelRatio }),
        // JPEG has no alpha channel; a transparent request would come out black.
        background: format === DrawingFormat.Jpeg ? (background ?? '#ffffff') : background,
        type: format === DrawingFormat.Jpeg ? 'image/jpeg' : 'image/png',
      };
      return renderToBlob(scene, raster);
    }
  }
}
